import type { CalDateInfo, SlotInfo } from '../../../types/schedule.types';
import type { SlotLocationVisibilityPatch } from '../../../utils/calDateSlotsSortUtils';

/**
 * 跨日拖拽后的 slot 列表。与日历乐观更新使用同一规则，供写入数据缓存。
 * 缓存若仍停留在拖拽前，下一次添加场次会用旧 slotDate 覆盖界面，场次会回到原日期。
 */
export function buildMovedSlotList(
  slots: SlotInfo[],
  idsToMove: string[],
  idsToDelete: string[],
  targetDateStr: string,
  visibilityPatches: SlotLocationVisibilityPatch[] = [],
): SlotInfo[] {
  const deleteSet = new Set(idsToDelete);
  const moveSet = new Set(idsToMove);
  const patchMap = new Map(visibilityPatches.map((patch) => [patch.slotID, patch]));
  return slots
    .filter((slot) => !deleteSet.has(slot.slotID))
    .map((slot) => {
      const patch = patchMap.get(slot.slotID);
      if (!moveSet.has(slot.slotID) && !patch) return slot;
      return {
        ...slot,
        ...(moveSet.has(slot.slotID)
          ? { slotDate: targetDateStr, slotEndDay: targetDateStr }
          : {}),
        ...(patch
          ? {
              slotLocationVisibility: patch.slotLocationVisibility,
              ...(patch.slotShift !== undefined ? { slotShift: patch.slotShift } : {}),
            }
          : {}),
      };
    });
}

export function buildMovedCalDateList(
  calDates: CalDateInfo[],
  params: {
    scheduleID: string;
    sourceDateStr: string;
    targetDateStr: string;
    newSourceFlat: string[];
    newTargetFlat: string[];
    removeSourceDate: boolean;
    targetShift?: string;
  },
): CalDateInfo[] {
  let hasTargetDate = false;
  const updated = calDates
    .filter((pd) => {
      if (!params.removeSourceDate || !pd.calDateDt) return true;
      return pd.calDateDt.split('T')[0] !== params.sourceDateStr;
    })
    .map((pd) => {
      if (!pd.calDateDt) return pd;
      const pdDateStr = pd.calDateDt.split('T')[0];
      if (!params.removeSourceDate && pdDateStr === params.sourceDateStr) {
        return { ...pd, calDateSlotsSort: params.newSourceFlat };
      }
      if (pdDateStr === params.targetDateStr) {
        hasTargetDate = true;
        return {
          ...pd,
          calDateSlotsSort: params.newTargetFlat,
          ...(params.targetShift !== undefined ? { calDateShift: params.targetShift } : {}),
        };
      }
      return pd;
    });
  if (!hasTargetDate) {
    updated.push({
      calDateID: `temp-${params.targetDateStr}`,
      calDateDt: params.targetDateStr,
      calDateBelongScheduleID: params.scheduleID,
      calDateSlotsSort: params.newTargetFlat,
      ...(params.targetShift !== undefined ? { calDateShift: params.targetShift } : {}),
    } as CalDateInfo);
  }
  return updated;
}

/** 用服务端返回的真实 ID 和更新时间替换乐观数据，供下一次并发校验使用。 */
export function mergeServerCalDates(
  optimisticCalDates: CalDateInfo[],
  serverCalDates: CalDateInfo[],
): CalDateInfo[] {
  const serverByDate = new Map(
    serverCalDates
      .map((calDate) => [calDate.calDateDt?.split('T')[0], calDate] as const)
      .filter((entry): entry is readonly [string, CalDateInfo] => !!entry[0]),
  );
  return optimisticCalDates.map((calDate) => {
    const dateStr = calDate.calDateDt?.split('T')[0];
    return (dateStr && serverByDate.get(dateStr)) || calDate;
  });
}

/**
 * 用拖拽后的列表覆盖缓存，并保留拖拽期间新出现、不在拖拽前快照里的记录（例如刚添加的场次）。
 */
export function commitCrossDateMoveCache(params: {
  nextSlots: SlotInfo[];
  baselineSlotIDs: ReadonlySet<string>;
  cacheSlots: SlotInfo[] | undefined;
  nextCalDates: CalDateInfo[];
  baselineDateStrs: ReadonlySet<string>;
  cacheCalDates: CalDateInfo[] | undefined;
}): { slots: SlotInfo[]; calDates: CalDateInfo[] } {
  const cacheSlots = params.cacheSlots ?? [];
  const extraSlots = cacheSlots.filter((slot) => !params.baselineSlotIDs.has(slot.slotID));
  const cacheCalDates = params.cacheCalDates ?? [];
  const extraCalDates = cacheCalDates.filter((pd) => {
    const dateStr = pd.calDateDt?.split('T')[0];
    return !!dateStr && !params.baselineDateStrs.has(dateStr);
  });
  return {
    slots: [...params.nextSlots, ...extraSlots],
    calDates: [...params.nextCalDates, ...extraCalDates],
  };
}
